import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ContractApiService } from "src/app/core/api/contract-api.service";
import { CurrencyApiService } from "src/app/core/api/currency-api.service";
import { forkJoin, of, switchMap } from "rxjs";
import {
    ClientSideRowModelModule,
    ModuleRegistry,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    PaginationModule,
    ColumnApiModule,
    ColumnAutoSizeModule,
    ColDef,
    CellClickedEvent,
    GridApi,
    RenderApiModule,
    ClientSideRowModelApiModule,
} from "ag-grid-community";
import { AgGridAngular } from "ag-grid-angular";
import { FormsModule } from "@angular/forms";
import { formatDate, NgClass } from "@angular/common";
import { ContractSmall } from "src/app/core/models/contract-small";
import { Contract } from "src/app/core/models/contract";
import { ContractCommentsComponent } from "./components/contract-comments/contract-comments.component";
import { ContractCurrency } from "src/app/core/models/contract-currency";
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { Extension, ExtensionStatus } from "src/app/core/models/extension";
import { ExtensionApiService } from "src/app/core/api/extension-api.service";
import { ContractDetailsApiService } from "src/app/core/api/contract-details-api.service";
import {
    StatusCellRendererComponent,
    StatusOption,
} from "./components/status-cell-renderer/status-cell-renderer.component";
import { ExtensionActionsComponent } from "./components/extension-actions/extension-actions.component";
import { ConfirmExtensionModalComponent } from "./components/confirm-extension-modal/confirm-extension-modal.component";
import { TaskContractModalComponent } from "src/app/tasks/components/task-contract-modal/task-contract-modal.component";

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    PaginationModule,
    ColumnApiModule,
    ColumnAutoSizeModule,
    RenderApiModule,
    ClientSideRowModelApiModule,
]);

/** Grid row for the extensions table; the first row is a synthetic "original contract" row. */
type ExtensionRow = Extension & { isOriginalContract?: boolean };

@Component({
    selector: "app-contract-profile",
    standalone: true,
    imports: [
        TranslatePipe,
        AgGridAngular,
        ContractCommentsComponent,
        NgClass,
        FormsModule,
    ],
    templateUrl: "./contract-profile.component.html",
    styleUrl: "./contract-profile.component.scss",
})
export class ContractProfileComponent implements OnInit {
    public contractTableTitles: any = {};
    contractId: number;
    public contractsByProfileAndCustomer: ContractSmall[] = [];
    public contractByProfile: ContractSmall[] = [];
    public colDefs: ColDef<ContractSmall>[] = [];
    public colDefsByProgileId: ColDef<ContractSmall>[] = [];
    public showTab: "Extensions" | "Comments" = "Extensions";
    public consultantName: string;
    public customerName: string;
    public taskTitle: string;
    public jobId: number;
    public contract: Contract;
    public contractCurrency: ContractCurrency | null = null;
    public contractCurrencies: ContractCurrency[] = [];
    public currencyColDefs: ColDef<ContractCurrency>[] = [];

    // Contract info grid (single-row summary, replaces the old bootstrap table)
    public contractInfoRows: Contract[] = [];
    public infoColDefs: ColDef<Contract>[] = [];
    public infoDefaultColDef = {
        sortable: false,
        filter: false,
        resizable: true,
        suppressMovable: true,
        flex: 1,
        minWidth: 120,
        valueFormatter: (params: any) => params.value || "-",
    };

    // Extensions grid
    public extensionRows: ExtensionRow[] = [];
    private allExtensionRows: ExtensionRow[] = [];
    public selectedExtensionStatus: number | null = null;
    public extensionColDefs: ColDef<ExtensionRow>[] = [];
    public statusOptions: StatusOption[] = [];
    private extensionGridApi: GridApi<ExtensionRow>;
    private currencyGridApi?: GridApi<ContractCurrency>;
    public extensionGridContext = { parentComponent: this };
    public extensionDefaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
    };
    // ContractCurrencies grid: no filtering of any kind.
    public currencyDefaultColDef = {
        sortable: true,
        filter: false,
        resizable: true,
        floatingFilter: false,
        suppressHeaderFilterButton: true,
    };
    public columnVisibility: { field: string; headerName: string; visible: boolean }[] = [];
    public showColumnPanel = false;
    // Columns that must always stay visible (cannot be toggled off).
    private readonly lockedColumns = ["status", "createdDate", "contractDetailsCount", "actions"];

    constructor(
        private contractApiSerivice: ContractApiService,
        private currencyApiService: CurrencyApiService,
        private extensionApiService: ExtensionApiService,
        private contractDetailsApiService: ContractDetailsApiService,
        private modalService: NgbModal,
        private toastr: ToastrService,
        private route: ActivatedRoute,
        private router: Router,
        private translateService: TranslateService
    ) {}

    defaultColDef = {
        sortable: false,
        filter: false,
        resizable: false,
        floatingFilter: false,
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
        suppressColumnVirtualisation: true,
        suppressSizeToFit: true,
        suppressMovable: true,
    };

    ngOnInit(): void {
        this.contractId = Number(
            this.route.snapshot.paramMap.get("contractId")
        );
        this.translateService.stream("contracts").subscribe((res) => {
            this.contractTableTitles = res;
            this.initializeColumnDefs();
            this.initializeProfileInfoColumnDefs();
            this.initializeInfoColumnDefs();
            this.buildStatusOptions();
            this.initializeExtensionColumnDefs();
        });

        this.loadContractDetails(this.contractId);
        this.contractApiSerivice
            .getContractCurrency(this.contractId)
            .subscribe((result) => {
                this.contractCurrency = result;
            });
        this.currencyApiService
            .getContractCurrencies(this.contractId)
            .subscribe((rows) => {
                this.contractCurrencies = rows;
                this.buildCurrencyColDefs();
            });
    }

    public loadContractDetails(contractId: number) {
        if (!contractId) return;

        this.contractApiSerivice
            .getContractByContractId(contractId)
            .pipe(
                switchMap((contract) => {
                    this.contract = contract;
                    this.contractInfoRows = [contract];
                    this.consultantName = contract.fullname;
                    this.customerName = contract.customerName;
                    this.taskTitle = contract.taskSmall.title;
                    this.jobId = contract.taskSmall.jobId;

                    return forkJoin({
                        contractsByProfileAndCustomer: contract.customerId
                            ? this.contractApiSerivice.getContractsByProfileAndCustomer(
                                  contract.profileId,
                                  contract.customerId
                              )
                            : of([]),
                        contractByProfile:
                            this.contractApiSerivice.getContracts(
                                contract.profileId
                            ),
                    });
                })
            )
            .subscribe({
                next: (result) => {
                    this.contractsByProfileAndCustomer =
                        result.contractsByProfileAndCustomer;
                    this.contractByProfile = result.contractByProfile;
                    this.loadExtensions();
                },
                error: (error) => {},
            });
    }

    private loadExtensions() {
        forkJoin({
            extensions: this.extensionApiService.getByContract(this.contractId),
            contractDetails: this.contractDetailsApiService.getByOwner({
                contractId: this.contractId,
            }),
        }).subscribe({
            next: ({ extensions, contractDetails }) => {
                const originalRow: ExtensionRow = {
                    ...this.contractToExtensionPrefill(),
                    id: this.contractId,
                    contractId: this.contractId,
                    status: ExtensionStatus.Confirmed,
                    createdDate: this.contract?.createdDate,
                    contractDetailsCount: contractDetails.length,
                    isOriginalContract: true,
                };
                this.allExtensionRows = [originalRow, ...extensions];
                this.applyExtensionStatusFilter();
                // Currency titles depend on allExtensionRows; refresh once it's populated.
                this.currencyGridApi?.refreshCells({ force: true });
            },
        });
    }

    public applyExtensionStatusFilter() {
        this.extensionRows =
            this.selectedExtensionStatus === null
                ? this.allExtensionRows
                : this.allExtensionRows.filter(
                      (r) => r.status === this.selectedExtensionStatus
                  );
        this.extensionGridApi?.setGridOption("rowData", this.extensionRows);
    }

    private contractToExtensionPrefill(): Extension {
        const c = this.contract;
        return {
            id: 0,
            contractId: this.contractId,
            status: ExtensionStatus.Draft,
            purchasePrice: c?.purchasePrice,
            soldPrice: c?.soldPrice,
            travelPrice: c?.travelPrice,
            overtimePrice: c?.overtimePrice,
            costPrice: c?.costPrice,
            margin: c?.margin,
            weeklyHours: c?.weeklyHours,
            description: c?.description,
            reg: c?.reg,
            account: c?.account,
            iban: c?.iban,
            swift: c?.swift,
            currency: c?.currency,
            equipment: c?.equipment,
            startDate: c?.startDate,
            endDate: c?.endDate,
            paymentTermsIsKnown: false,
            option: c?.option ? Number(c.option) : undefined,
            contractDetailsCount: 0,
        };
    }

    private buildStatusOptions() {
        this.statusOptions = [
            { value: ExtensionStatus.Draft, label: this.translateService.instant("extensions.status.draft") },
            { value: ExtensionStatus.InProgress, label: this.translateService.instant("extensions.status.inProgress") },
            { value: ExtensionStatus.AwaitingApproval, label: this.translateService.instant("extensions.status.awaitingApproval") },
            { value: ExtensionStatus.Confirmed, label: this.translateService.instant("extensions.status.confirmed") },
            { value: ExtensionStatus.Declined, label: this.translateService.instant("extensions.status.declined") },
            { value: ExtensionStatus.Failed, label: this.translateService.instant("extensions.status.failed") },
            { value: ExtensionStatus.Cancelled, label: this.translateService.instant("extensions.status.cancelled") },
        ];
    }

    private formatGridDate = (params: any) =>
        params.value ? formatDate(params.value, "dd-MM-yyyy", "en") : "";

    private dateFilterGetter = (field: "startDate" | "endDate" | "createdDate") =>
        (params: any) =>
            params.data?.[field]
                ? formatDate(params.data[field], "dd-MM-yyyy", "en")
                : "";

    private initializeExtensionColumnDefs() {
        const t = this.contractTableTitles;
        this.extensionColDefs = [
            {
                colId: "title",
                headerName: this.translateService.instant("extensions.titleColumn") ?? "Title",
                width: 160,
                filter: false,
                sortable: false,
                valueGetter: (params: any) => {
                    const row: ExtensionRow = params.data;
                    if (!row) return '';
                    if (row.isOriginalContract) {
                        return this.translateService.instant("extensions.initialContract");
                    }
                    const extensions = this.allExtensionRows.filter(r => !r.isOriginalContract);
                    const index = extensions.findIndex(r => r.id === row.id);
                    const number = index >= 0 ? index + 1 : '?';
                    return this.translateService.instant("extensions.extensionLabel", { number });
                },
            },
            {
                field: "status",
                headerName: t.extensionStatus ?? "Status",
                width: 200,
                filter: false,
                cellRenderer: StatusCellRendererComponent,
                cellRendererParams: {
                    statusOptions: this.statusOptions,
                    onStatusChange: this.onStatusChange,
                    isLocked: this.isLocked,
                },
            },
            {
                field: "createdDate",
                headerName: t.createdDate ?? "Created",
                valueFormatter: this.formatGridDate,
                filterValueGetter: this.dateFilterGetter("createdDate"),
                filter: "agTextColumnFilter",
                flex: 1,
                minWidth: 110,
                sort: "asc",
            },
            {
                field: "startDate",
                headerName: t.startDate,
                valueFormatter: this.formatGridDate,
                filterValueGetter: this.dateFilterGetter("startDate"),
                filter: "agTextColumnFilter",
                flex: 1,
                minWidth: 110,
            },
            {
                field: "endDate",
                headerName: t.endDate,
                valueFormatter: this.formatGridDate,
                filterValueGetter: this.dateFilterGetter("endDate"),
                filter: "agTextColumnFilter",
                flex: 1,
                minWidth: 110,
            },
            {
                field: "purchasePrice",
                headerName: t.purchasePrice,
                filter: "agTextColumnFilter",
                flex: 1,
                minWidth: 110,
            },
            {
                field: "soldPrice",
                headerName: t.soldPrice,
                filter: "agTextColumnFilter",
                flex: 1,
                minWidth: 110,
            },
            {
                field: "weeklyHours",
                headerName: t.weeklyHours ?? "Weekly hours",
                filter: "agTextColumnFilter",
                flex: 1,
                minWidth: 110,
            },
            // Optional columns - hidden by default, toggled via the column options panel.
            { field: "travelPrice", headerName: t.travelPrice ?? "Travel price", filter: "agTextColumnFilter", flex: 1, minWidth: 110, hide: true },
            { field: "overtimePrice", headerName: t.overtimePrice ?? "Overtime price", filter: "agTextColumnFilter", flex: 1, minWidth: 110, hide: true },
            { field: "costPrice", headerName: t.costPrice ?? "Cost price", filter: "agTextColumnFilter", flex: 1, minWidth: 110, hide: true },
            { field: "margin", headerName: t.margin ?? "Margin", filter: "agTextColumnFilter", flex: 1, minWidth: 110, hide: true },
            { field: "currency", headerName: t.currency ?? "Currency", filter: "agTextColumnFilter", flex: 1, minWidth: 100, hide: true },
            { field: "equipment", headerName: t.equipment ?? "Equipment", filter: "agTextColumnFilter", flex: 1, minWidth: 110, hide: true },
            { field: "option", headerName: t.option ?? "Option", filter: "agTextColumnFilter", flex: 1, minWidth: 100, hide: true },
            { field: "reg", headerName: t.reg ?? "Reg", filter: "agTextColumnFilter", flex: 1, minWidth: 100, hide: true },
            { field: "account", headerName: t.account ?? "Account", filter: "agTextColumnFilter", flex: 1, minWidth: 110, hide: true },
            {
                field: "contractDetailsCount",
                headerName: t.contractDetails ?? "Contract details",
                width: 160,
                filter: false,
                cellClass: "link-cell",
                cellRenderer: (params: any) =>
                    `<span class="link-cell-content">${params.value ?? 0} <i class="fa fa-chevron-right link-arrow"></i></span>`,
                onCellClicked: (event: CellClickedEvent<ExtensionRow>) => {
                    if (event.data) {
                        this.goToContractDetails(event.data);
                    }
                },
            },
            {
                colId: "actions",
                headerName: t.actions ?? "Actions",
                cellRenderer: ExtensionActionsComponent,
                cellRendererParams: { context: this.extensionGridContext },
                filter: false,
                sortable: false,
                width: 110,
                pinned: "right",
                resizable: false,
                lockPinned: true,
                lockPosition: true,
            },
        ];

        // Build the toggle list for the column options panel (skip the actions column).
        this.columnVisibility = this.extensionColDefs
            .filter((c) => (c.field || (c as any).colId) && (c as any).colId !== "actions")
            .map((c) => ({
                field: (c.field as string) ?? "",
                headerName: (c.headerName as string) ?? "",
                visible: !c.hide,
            }));
    }

    public toggleColumnPanel() {
        this.showColumnPanel = !this.showColumnPanel;
    }

    public toggleColumnVisibility(field: string) {
        if (this.lockedColumns.includes(field)) return;
        const state = this.columnVisibility.find((c) => c.field === field);
        if (state && this.extensionGridApi) {
            state.visible = !state.visible;
            this.extensionGridApi.setColumnsVisible([field], state.visible);
        }
    }

    public isLockedColumn(field: string): boolean {
        return this.lockedColumns.includes(field);
    }

    public onExtensionGridReady(params: any) {
        this.extensionGridApi = params.api;
        this.extensionGridApi.setGridOption("rowData", this.extensionRows);
    }

    public getExtensionRowClass = (params: any) =>
        params.data?.isOriginalContract ? "original-contract-row" : "";

    private isLocked = (data: ExtensionRow) =>
        data.status === ExtensionStatus.Confirmed;

    private onStatusChange = (
        data: ExtensionRow,
        newStatus: ExtensionStatus,
        revert: () => void
    ) => {
        if (newStatus === ExtensionStatus.Confirmed) {
            const modalRef = this.modalService.open(
                ConfirmExtensionModalComponent,
                { centered: true, backdrop: true, size: "xl" }
            );
            modalRef.componentInstance.extensionId = data.id;
            modalRef.result.then(
                (confirmed: boolean) => {
                    if (confirmed) {
                        this.applyStatus(data, newStatus, revert);
                    } else {
                        revert();
                        this.extensionGridApi?.refreshCells({ force: true });
                    }
                },
                () => {
                    revert();
                    this.extensionGridApi?.refreshCells({ force: true });
                }
            );
        } else {
            this.applyStatus(data, newStatus, revert);
        }
    };

    private applyStatus(
        data: ExtensionRow,
        newStatus: ExtensionStatus,
        revert: () => void
    ) {
        this.extensionApiService.patchStatus(data.id, newStatus).subscribe({
            next: (updated) => {
                data.status = updated.status;
                this.toastr.success(
                    this.translateService.instant("extensions.statusUpdated")
                );
                this.extensionGridApi?.refreshCells({ force: true });
            },
            error: (err) => {
                this.toastr.error(
                    err?.error ||
                        this.translateService.instant(
                            "extensions.statusUpdateError"
                        )
                );
                revert();
                this.extensionGridApi?.refreshCells({ force: true });
            },
        });
    }

    public goToContractDetails(row: ExtensionRow) {
        const ownerType = row.isOriginalContract ? "contract" : "extension";
        const ownerId = row.isOriginalContract ? this.contractId : row.id;
        this.router.navigate([
            "contract",
            this.contractId,
            "details",
            ownerType,
            ownerId,
            "contract-details",
        ]);
    }

    /** Builds a Contract used to prefill the (reused) task-contract modal form. */
    private mergeExtensionIntoContract(ext: Extension): Contract {
        const base: Contract = { ...(this.contract as any) } as Contract;
        base.purchasePrice = ext.purchasePrice ?? base.purchasePrice;
        base.soldPrice = ext.soldPrice ?? base.soldPrice;
        base.travelPrice = ext.travelPrice ?? base.travelPrice;
        base.overtimePrice = ext.overtimePrice ?? base.overtimePrice;
        base.costPrice = ext.costPrice ?? base.costPrice;
        base.margin = ext.margin ?? base.margin;
        base.weeklyHours = ext.weeklyHours ?? base.weeklyHours;
        base.description = ext.description ?? base.description;
        base.currency = ext.currency ?? base.currency;
        base.equipment = ext.equipment ?? base.equipment;
        base.reg = ext.reg ?? base.reg;
        base.account = ext.account ?? base.account;
        base.iban = ext.iban ?? base.iban;
        base.swift = ext.swift ?? base.swift;
        base.startDate = (ext.startDate as any) ?? base.startDate;
        base.endDate = (ext.endDate as any) ?? base.endDate;
        if (ext.option != null) {
            base.option = String(ext.option);
        }
        return base;
    }

    public openCreateExtension() {
        const confirmedExtensions = this.extensionRows.filter(
            (r) => !r.isOriginalContract && r.status === ExtensionStatus.Confirmed
        );
        const latestConfirmed = confirmedExtensions.length
            ? confirmedExtensions[confirmedExtensions.length - 1]
            : null;
        const prefillContract = latestConfirmed
            ? this.mergeExtensionIntoContract(latestConfirmed)
            : ({ ...(this.contract as any) } as Contract);

        const options: NgbModalOptions = { centered: true, backdrop: true, size: "lg" };
        const modalRef = this.modalService.open(TaskContractModalComponent, options);
        modalRef.componentInstance.extensionMode = true;
        modalRef.componentInstance.extensionContractId = this.contractId;
        modalRef.componentInstance.prefillContract = prefillContract;

        modalRef.result.then(
            (payload: Extension) => {
                if (!payload) return;
                this.extensionApiService.post(payload).subscribe({
                    next: () => {
                        this.toastr.success(
                            this.translateService.instant("extensions.form.created")
                        );
                        this.loadExtensions();
                    },
                    error: () =>
                        this.toastr.error(
                            this.translateService.instant("extensions.form.createdError")
                        ),
                });
            },
            () => {}
        );
    }

    public editExtension(data: ExtensionRow) {
        if (data.isOriginalContract) return;
        const options: NgbModalOptions = { centered: true, backdrop: true, size: "lg" };
        const extensions = this.extensionRows.filter((r) => !r.isOriginalContract);
        const number = extensions.findIndex((r) => r.id === data.id) + 1;

        const modalRef = this.modalService.open(TaskContractModalComponent, options);
        modalRef.componentInstance.extensionMode = true;
        modalRef.componentInstance.extensionContractId = this.contractId;
        modalRef.componentInstance.extension = data;
        modalRef.componentInstance.prefillContract = this.mergeExtensionIntoContract(data);
        modalRef.componentInstance.extensionLabel = this.translateService.instant(
            "extensions.extensionLabel",
            { number }
        );

        modalRef.result.then(
            (payload: Extension) => {
                if (!payload) return;
                this.extensionApiService.put(payload).subscribe({
                    next: () => {
                        this.toastr.success(
                            this.translateService.instant("extensions.form.updated")
                        );
                        this.loadExtensions();
                    },
                    error: () =>
                        this.toastr.error(
                            this.translateService.instant("extensions.form.updatedError")
                        ),
                });
            },
            () => {}
        );
    }

    public deleteExtension(data: ExtensionRow) {
        if (data.isOriginalContract) return;
        this.extensionApiService.delete(data.id).subscribe({
            next: () => {
                this.toastr.success(
                    this.translateService.instant("extensions.deleted")
                );
                this.loadExtensions();
            },
            error: () =>
                this.toastr.error(
                    this.translateService.instant("extensions.deleteError")
                ),
        });
    }

    public onRowClicked(event: { data: { id: any } }) {
        const navigateToContractId = event.data.id;
        this.contractId = navigateToContractId;
        // Navigate to a dummy route first, then to the target
        this.router
            .navigateByUrl("/", { skipLocationChange: true })
            .then(() => {
                this.router.navigate([
                    "contract",
                    navigateToContractId,
                    "details",
                ]);
            });
    }

    private initializeColumnDefs() {
        this.colDefs = [
            {
                field: "taskTitle",
                headerName: this.contractTableTitles.title,
                filter: false,
                flex: 1,
                resizable: true,
            },
            {
                field: "startDate",
                headerName: this.contractTableTitles.startDate,
                valueFormatter: (params) =>
                    formatDate(params.value, "dd-MM-yyyy", "en"),
                filter: false,
                width: 115,
                sort: "desc",
            },
            {
                field: "endDate",
                headerName: this.contractTableTitles.endDate,
                valueFormatter: (params) =>
                    formatDate(params.value, "dd-MM-yyyy", "en"),
                filter: false,
                width: 120,
            },
            {
                field: "purchasePrice",
                width: 120,
                headerName: this.contractTableTitles.purchasePrice,
            },
            {
                field: "soldPrice",
                width: 120,
                headerName: this.contractTableTitles.soldPrice,
            },
        ];
    }

    private initializeInfoColumnDefs() {
        this.infoColDefs = [
            { field: "contractConsultantName", headerName: this.translateService.instant("labels.contractConsultantName") },
            { field: "contractEmail", headerName: this.translateService.instant("labels.contractMail") },
            { field: "creditorName", headerName: this.translateService.instant("labels.creditorName") },
            { field: "consultingHouseContactPerson", headerName: this.translateService.instant("labels.consultingHouseContactPerson") },
            { field: "communicationLanguage", headerName: this.translateService.instant("labels.communicationLanguage") },
        ];
    }

    public goToTab(showTab: "Extensions" | "Comments") {
        this.showTab = showTab;
    }

    public goToTask() {
        if (this.contract?.taskId) {
            this.router.navigate(["tasks", this.contract.taskId]);
        }
    }

    public goToCustomer() {
        if (this.contract?.customerId) {
            this.router.navigate(["customers", this.contract.customerId]);
        }
    }

    public goToProfile() {
        if (this.contract?.profileId) {
            this.router.navigate(["search/profile/edit", this.contract.profileId]);
        }
    }

    private initializeProfileInfoColumnDefs() {
        this.colDefsByProgileId = [
            {
                field: "customer",
                headerName: this.contractTableTitles.customerName,
                filter: false,
                flex: 1,
                resizable: true,
            },
            {
                field: "taskTitle",
                headerName: this.contractTableTitles.title,
                flex: 1,
                resizable: true,
            },
            {
                field: "startDate",
                headerName: this.contractTableTitles.startDate,
                valueFormatter: (params) =>
                    formatDate(params.value, "dd-MM-yyyy", "en"),
                filter: false,
                width: 115,
                sort: "desc",
            },
            {
                field: "endDate",
                headerName: this.contractTableTitles.endDate,
                valueFormatter: (params) =>
                    formatDate(params.value, "dd-MM-yyyy", "en"),
                filter: false,
                width: 120,
            },
            {
                field: "purchasePrice",
                width: 120,
                headerName: this.contractTableTitles.purchasePrice,
            },
            {
                field: "soldPrice",
                width: 120,
                headerName: this.contractTableTitles.soldPrice,
            },
        ];
    }
    public getRowClass = (params: any) => {
        return params.data?.id === this.contractId
            ? "current-contract-row"
            : "";
    };

    public goBack() {
        this.router.navigate(["contracts-overview"]);
    }

    /** Returns a human-readable title label for a ContractCurrency row. */
    public getCurrencyRowTitle(cc: ContractCurrency): string {
        if (cc.contractId && !cc.extensionId) {
            return this.translateService.instant("extensions.initialContract");
        }
        if (cc.extensionId) {
            const extensions = this.allExtensionRows.filter(r => !r.isOriginalContract);
            const index = extensions.findIndex(r => r.id === cc.extensionId);
            const number = index >= 0 ? index + 1 : '?';
            return this.translateService.instant("extensions.extensionLabel", { number });
        }
        return '';
    }

    public onCurrencyGridReady(params: any) {
        this.currencyGridApi = params.api;
    }

    private buildCurrencyColDefs(): void {
        this.currencyColDefs = [
            {
                colId: "currencyTitle",
                headerName: this.translateService.instant("extensions.titleColumn") ?? "Title",
                width: 180,
                filter: false,
                sortable: false,
                valueGetter: (params: any) => this.getCurrencyRowTitle(params.data),
            },
            {
                field: "currency",
                headerName: this.translateService.instant("contractsDetails.altCurrencyBox.currency") ?? "Currency",
                filter: false,
                flex: 1,
                minWidth: 100,
            },
            {
                field: "exchangeRate",
                headerName: this.translateService.instant("contractsDetails.altCurrencyBox.exchangeRate") ?? "Exchange rate",
                filter: false,
                flex: 1,
                minWidth: 120,
            },
            {
                field: "price",
                headerName: this.translateService.instant("contractsDetails.altCurrencyBox.price") ?? "Price",
                filter: false,
                flex: 1,
                minWidth: 120,
            },
        ];
    }
}
